import { parseISO } from "date-fns";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
export default (client) => {
    const parseUtcDate = (dateText) => utcToZonedTime(parseISO(dateText), "UTC");
    const formatUtcDate = (date) => zonedTimeToUtc(date, "UTC").toISOString().replace("Z", "+00:00");
    const mapPlanConfig = (planConfig) => (Object.assign(Object.assign({}, planConfig), { planner_items: planConfig.planner_items.map((planner_item, index) => (Object.assign(Object.assign({}, planner_item), { index: index + 1 }))), planned_events: planConfig.planned_events.map((plannedEvent) => (Object.assign(Object.assign({}, plannedEvent), { start: parseUtcDate(plannedEvent.start), end: parseUtcDate(plannedEvent.end) }))), actual_events: planConfig.actual_events.map((actualEvent) => (Object.assign(Object.assign({}, actualEvent), { start: parseUtcDate(actualEvent.start), end: actualEvent.end ? parseUtcDate(actualEvent.end) : null }))) }));
    const formatPlanConfigDates = (planConfig) => (Object.assign(Object.assign({}, planConfig), { planned_events: planConfig.planned_events.map((plannedEvent) => (Object.assign(Object.assign({}, plannedEvent), { start: formatUtcDate(plannedEvent.start), end: formatUtcDate(plannedEvent.end) }))), actual_events: planConfig.actual_events.map((actualEvent) => (Object.assign(Object.assign({}, actualEvent), { start: formatUtcDate(actualEvent.start), end: actualEvent.end ? formatUtcDate(actualEvent.end) : null }))) }));
    const formatActualEventChangesDates = (changes) => (Object.assign(Object.assign({}, changes), { added: changes.added.map((change) => (Object.assign(Object.assign({}, change), { start: formatUtcDate(change.start), end: change.end ? formatUtcDate(change.end) : null }))), modified: changes.modified.map((change) => (Object.assign(Object.assign({}, change), { start: formatUtcDate(change.start), end: change.end ? formatUtcDate(change.end) : null }))) }));
    const loadPlanConfig = (customerName, siteId) => {
        return client
            .get(`/planner-v2/plans/${customerName}/${siteId}`)
            .then((response) => mapPlanConfig(response.data));
    };
    const parseXML = (customerName, siteId, file, slackNotification) => {
        const formData = new FormData();
        formData.append("file", file);
        const searchParams = new URLSearchParams();
        slackNotification && searchParams.set("slackNotification", slackNotification);
        return client
            .post(`/planner-v2/plans/${customerName}/${siteId}/parse?${searchParams.toString()}`, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        })
            .then((response) => mapPlanConfig(response.data));
    };
    const saveActualEventChanges = (customerName, siteId, changes) => client
        .post(`/planner-v2/actual-events/${customerName}/${siteId}`, formatActualEventChangesDates(changes))
        .then((response) => response.data);
    const loadActualEventsChanges = (customerName, siteId) => client
        .get(`/planner-v2/actual-events/${customerName}/${siteId}/changes`)
        .then((response) => response.data);
    const updateActualEventsToEndTracking = (customerName, siteId) => client
        .post(`/planner-v2/actual-events/end-tracking/${customerName}/${siteId}`)
        .then((response) => response.data);
    const createNewRevision = (customerName, siteId, planConfig, mergedPlannerItems) => client
        .post(`/planner-v2/plan-revisions/${customerName}/${siteId}`, {
        plan_config: formatPlanConfigDates(planConfig),
        merged_planner_items: mergedPlannerItems,
    })
        .then((response) => mapPlanConfig(response.data));
    const loadPlanRevisions = (customerName, siteId) => client
        .get(`/planner-v2/plan-revisions/${customerName}/${siteId}`)
        .then((response) => response.data);
    const deletePlanRevision = (customerName, siteId, planId) => client
        .delete(`/planner-v2/plan-revisions/${customerName}/${siteId}/${planId}`)
        .then((response) => {
        if (response.status !== 200) {
            throw new Error("Unsuccessful status code");
        }
    });
    const activatePlan = (customerName, siteId, planId) => client
        .post(`/planner-v2/plan-revisions/${customerName}/${siteId}/${planId}/activate`)
        .then((response) => mapPlanConfig(response.data));
    const loadPlanExportData = (customerName, siteId) => client
        .get(`/planner-v2/plans/${customerName}/${siteId}/export-data`)
        .then((response) => response.data.map((obj) => (Object.assign(Object.assign({}, obj), { planned_start: obj.planned_start ? parseUtcDate(obj.planned_start) : null, planned_end: obj.planned_end ? parseUtcDate(obj.planned_end) : null, actual_start: obj.actual_start ? parseUtcDate(obj.actual_start) : null, actual_end: obj.actual_end ? parseUtcDate(obj.actual_end) : null }))));
    const calculateProcessBreaks = (process) => process.work_intervals.reduce((acc, work_interval) => {
        if (acc.last_start_time === null) {
            return Object.assign(Object.assign({}, acc), { last_start_time: work_interval.end_time });
        }
        return {
            breaks: [
                ...acc.breaks,
                { start_time: acc.last_start_time, end_time: work_interval.start_time },
            ],
            last_start_time: work_interval.end_time,
        };
    }, { breaks: [], last_start_time: null }).breaks;
    const loadProcesses = (customerName, siteId) => client
        .get(`/planner-v2/processes/${customerName}/${siteId}`)
        .then((response) => response.data.processes.map((process) => {
        const mappedProcess = Object.assign(Object.assign({}, process), { start_time: parseUtcDate(process.start_time), end_time: parseUtcDate(process.end_time), work_intervals: process.work_intervals.map((workInterval) => (Object.assign(Object.assign({}, workInterval), { start_time: parseUtcDate(workInterval.start_time), end_time: parseUtcDate(workInterval.end_time) }))) });
        return Object.assign(Object.assign({}, mappedProcess), { breaks: calculateProcessBreaks(mappedProcess) });
    }));
    const loadResourceCameraIds = (customerName, siteId, sourceId) => client
        .get(`/planner-v2/section-masks/camera-ids/${customerName}/${siteId}/${encodeURIComponent(sourceId)}`)
        .then((response) => response.data);
    return {
        loadPlanConfig,
        parseXML,
        saveActualEventChanges,
        updateActualEventsToEndTracking,
        createNewRevision,
        loadActualEventsChanges,
        loadPlanRevisions,
        deletePlanRevision,
        activatePlan,
        loadPlanExportData,
        loadProcesses,
        calculateProcessBreaks,
        loadResourceCameraIds,
    };
};
//# sourceMappingURL=PlannerRepository.js.map